import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
  Alert,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Preview as PreviewIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import TinyMCEEditor from '../TinyMCEEditor';
import { templatesAPI } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import { useSettings } from '../../contexts/SettingsContext';



const schema = yup.object({
  name: yup.string().required('Template name is required').max(100, 'Name must be less than 100 characters'),
  description: yup.string().max(500, 'Description must be less than 500 characters'),
  category: yup.string().required('Category is required'),
  content: yup.string().required('Template content is required'),
  variables: yup.array().of(
    yup.object({
      name: yup.string().required('Variable name is required'),
      type: yup.string().required('Variable type is required'),
      defaultValue: yup.string(),
      description: yup.string()
    })
  ),
  sections: yup.array().of(
    yup.object({
      title: yup.string().required('Section title is required'),
      content: yup.string().required('Section content is required')
    })
  ),
  isActive: yup.boolean()
});

const TemplateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { settings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [templateData, setTemplateData] = useState(null);

  // Determine mode based on URL
  const isEdit = window.location.pathname.includes('/edit');
  const isView = window.location.pathname.includes('/view') || (id && !isEdit && !window.location.pathname.includes('/create'));
  const isCreate = !id || window.location.pathname.includes('/create');

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      category: '',
      content: '',
      variables: [],
      sections: [],
      isActive: true
    }
  });

  const { fields: variableFields, append: appendVariable, remove: removeVariable } = useFieldArray({
    control,
    name: 'variables'
  });

  const { fields: sectionFields, append: appendSection, remove: removeSection } = useFieldArray({
    control,
    name: 'sections'
  });

  const watchedContent = watch('content');

  useEffect(() => {
    if ((isEdit || isView) && id) {
      fetchTemplate();
    }
  }, [id, isEdit, isView]);

  const fetchTemplate = async () => {
    try {
      setInitialLoading(true);
      const response = await templatesAPI.getProposalTemplate(id);
      const template = response.data.template;
      setTemplateData(template);
      
      // Only reset form for edit mode
      if (isEdit) {
        reset({
          name: template.name || '',
          description: template.description || '',
          category: template.category || '',
          content: template.content || '',
          variables: template.variables || [],
          sections: template.sections || [],
          isActive: template.isActive !== undefined ? template.isActive : true
        });
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.error('Failed to load template');
    } finally {
      setInitialLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (isEdit) {
        await templatesAPI.updateProposalTemplate(id, data);
        toast.success('Template updated successfully');
      } else {
        await templatesAPI.createProposalTemplate(data);
        toast.success('Template created successfully');
      }
      navigate('/templates');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/templates');
  };

  const handlePreview = () => {
    const content = isView ? templateData?.content : watchedContent;
    if (content) {
      setPreviewContent(content);
      setPreviewOpen(true);
    }
  };

  const addVariable = () => {
    appendVariable({
      name: '',
      type: 'text',
      defaultValue: '',
      description: ''
    });
  };

  const addSection = () => {
    appendSection({
      title: '',
      content: ''
    });
  };

  const getCategoryLabel = (category) => {
    const categories = {
      service: 'Service',
      product: 'Product',
      consulting: 'Consulting',
      maintenance: 'Maintenance',
      other: 'Other'
    };
    return categories[category] || category;
  };



  if (initialLoading) {
    return <LoadingSpinner />;
  }

  // View Mode - Clean, formatted display
  if (isView && templateData) {
    return (
      <Box sx={{ p: 3, maxWidth: 'none', width: '100%' }}>
        {/* Header */}
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: { xs: 2, md: 0 }
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ wordBreak: 'break-word' }}>
              {templateData.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={getCategoryLabel(templateData.category)} 
                color="primary" 
                variant="outlined" 
                size="small"
              />
              <Chip 
                label={templateData.isActive ? 'Active' : 'Inactive'} 
                color={templateData.isActive ? 'success' : 'default'} 
                size="small"
              />
            </Box>
            {templateData.description && (
              <Typography variant="body1" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {templateData.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            width: { xs: '100%', md: 'auto' },
            justifyContent: { xs: 'flex-start', md: 'flex-end' }
          }}>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={handlePreview}
              size="small"
            >
              Preview
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/templates/${id}/edit`)}
              size="small"
            >
              Edit Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              size="small"
            >
              Back
            </Button>
          </Box>
        </Box>

        {/* Template Content */}
        <Paper sx={{ p: 4, mb: 3, maxWidth: 'none', width: '100%' }}>
        <Grid container spacing={3}>
          {/* Template Content */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Template Content
                </Typography>
                <Box 
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1, 
                    p: 2,
                    backgroundColor: 'background.paper',
                    minHeight: '200px'
                  }}
                  dangerouslySetInnerHTML={{ __html: templateData.content }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Variables */}
          {templateData.variables && templateData.variables.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Template Variables ({templateData.variables.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {templateData.variables.map((variable, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            {variable.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip label={variable.type} size="small" variant="outlined" />
                            {variable.defaultValue && (
                              <Chip label={`Default: ${variable.defaultValue}`} size="small" color="secondary" variant="outlined" />
                            )}
                          </Box>
                          {variable.description && (
                            <Typography variant="body2" color="text.secondary">
                              {variable.description}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Sections */}
          {templateData.sections && templateData.sections.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Template Sections ({templateData.sections.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {templateData.sections.map((section, index) => (
                      <Paper key={index} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                        <Typography variant="h6" gutterBottom>
                          {section.title}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box 
                          dangerouslySetInnerHTML={{ __html: section.content }}
                          sx={{ 
                            '& .ql-editor': { 
                              padding: 0,
                              border: 'none'
                            }
                          }}
                        />
                      </Paper>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Template Metadata */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Template Information
                </Typography>
                <Grid container spacing={2}>

                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Created Date
                    </Typography>
                    <Typography variant="body1">
                      {templateData.createdAt ? new Date(templateData.createdAt).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }) : 'Unknown'}
                    </Typography>
                  </Grid>
                  {templateData.modifier && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Modified By
                      </Typography>
                      <Typography variant="body1">
                        {`${templateData.modifier.firstName} ${templateData.modifier.lastName}`}
                      </Typography>
                    </Grid>
                  )}
                  {templateData.updatedAt && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Modified Date
                      </Typography>
                      <Typography variant="body1">
                        {new Date(templateData.updatedAt).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Paper>

        {/* Preview Dialog */}
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { height: '80vh' }
          }}
        >
          <DialogTitle>
            Template Preview
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                backgroundColor: 'background.paper',
                height: '100%',
                overflow: 'auto'
              }}
              dangerouslySetInnerHTML={{ __html: previewContent }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Edit/Create Mode - Form interface
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 'none', width: '100%' }}>
      {/* Header */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 2, md: 0 }
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {isEdit ? 'Edit Template' : 'Create New Template'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isEdit ? 'Update template information and content' : 'Create a reusable proposal template'}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flexWrap: 'wrap',
          width: { xs: '100%', md: 'auto' },
          justifyContent: { xs: 'flex-start', md: 'flex-end' }
        }}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
            disabled={!watchedContent}
            size="small"
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit(onSubmit)}
            disabled={loading}
            size="small"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
          </Button>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Template Name"
                          fullWidth
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          placeholder="Enter template name"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="category"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.category}>
                          <InputLabel>Category</InputLabel>
                          <Select {...field} label="Category">
                            <MenuItem value="service">Service</MenuItem>
                            <MenuItem value="product">Product</MenuItem>
                            <MenuItem value="consulting">Consulting</MenuItem>
                            <MenuItem value="maintenance">Maintenance</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                          {errors.category && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                              {errors.category.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Description"
                          fullWidth
                          multiline
                          rows={3}
                          error={!!errors.description}
                          helperText={errors.description?.message}
                          placeholder="Enter template description"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="isActive"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={field.onChange}
                            />
                          }
                          label="Active Template"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Template Content */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Template Content
                </Typography>
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <TinyMCEEditor
                      value={field.value || ''}
                      onChange={field.onChange}
                      placeholder="Enter your template content here..."
                      height={800}
                      error={!!errors.content}
                      helperText={errors.content?.message}
                    />
                  )}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Variables */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Template Variables
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addVariable}
                    size="small"
                  >
                    Add Variable
                  </Button>
                </Box>
                {variableFields.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No variables defined. Click "Add Variable" to create template variables.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {variableFields.map((field, index) => (
                      <Paper key={field.id} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2">
                            Variable {index + 1}
                          </Typography>
                          <IconButton
                            onClick={() => removeVariable(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={3}>
                            <Controller
                              name={`variables.${index}.name`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Variable Name"
                                  fullWidth
                                  size="small"
                                  error={!!errors.variables?.[index]?.name}
                                  helperText={errors.variables?.[index]?.name?.message}
                                  placeholder="e.g., clientName"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Controller
                              name={`variables.${index}.type`}
                              control={control}
                              render={({ field }) => (
                                <FormControl fullWidth size="small">
                                  <InputLabel>Type</InputLabel>
                                  <Select {...field} label="Type">
                                    <MenuItem value="text">Text</MenuItem>
                                    <MenuItem value="number">Number</MenuItem>
                                    <MenuItem value="date">Date</MenuItem>
                                    <MenuItem value="email">Email</MenuItem>
                                    <MenuItem value="phone">Phone</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Controller
                              name={`variables.${index}.defaultValue`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Default Value"
                                  fullWidth
                                  size="small"
                                  placeholder="Optional default value"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Controller
                              name={`variables.${index}.description`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Description"
                                  fullWidth
                                  size="small"
                                  placeholder="Variable description"
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sections */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Template Sections
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addSection}
                    size="small"
                  >
                    Add Section
                  </Button>
                </Box>
                {sectionFields.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No sections defined. Click "Add Section" to create template sections.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {sectionFields.map((field, index) => (
                      <Paper key={field.id} sx={{ p: 2, backgroundColor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2">
                            Section {index + 1}
                          </Typography>
                          <IconButton
                            onClick={() => removeSection(index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Controller
                              name={`sections.${index}.title`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Section Title"
                                  fullWidth
                                  error={!!errors.sections?.[index]?.title}
                                  helperText={errors.sections?.[index]?.title?.message}
                                  placeholder="Enter section title"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Controller
                              name={`sections.${index}.content`}
                              control={control}
                              render={({ field }) => (
                                <TinyMCEEditor
                                  value={field.value || ''}
                                  onChange={field.onChange}
                                  placeholder="Enter section content..."
                                  height={800}
                                  error={!!errors.sections?.[index]?.content}
                                  helperText={errors.sections?.[index]?.content?.message}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          Template Preview
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              backgroundColor: 'background.paper',
              height: '100%',
              overflow: 'auto'
            }}
            dangerouslySetInnerHTML={{ __html: previewContent }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateForm;